import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, Info, Activity, Heart, Target as TargetIcon } from 'lucide-react';
import { useHabitStore } from '../../store/habitStore';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export function HabitAnalytics() {
  const { habits } = useHabitStore();

  // Calculate completion rate over time
  const completionData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Completion Rate',
      data: [65, 75, 82, 90],
      borderColor: 'hsl(var(--primary))',
      tension: 0.4,
      fill: false
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(var(--muted-foreground), 0.1)'
        },
        ticks: {
          callback: (value: number) => `${value}%`,
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          }
        }
      }
    }
  };

  // Calculate overall stats
  const stats = {
    totalHabits: habits.length,
    activeStreak: Math.max(...habits.map(h => h.currentStreak)),
    completionRate: Math.round(
      (habits.reduce((sum, h) => sum + h.completedDays, 0) / 
      habits.reduce((sum, h) => sum + h.targetDays, 0)) * 100
    ),
    bestPerforming: habits.sort((a, b) => b.completedDays - a.completedDays)[0]?.name
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="theme-card p-4 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <TargetIcon className="w-4 h-4" />
              <span>Total Habits</span>
            </div>
            <div className="text-2xl font-bold">{stats.totalHabits}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Active tracking
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="theme-card p-4 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-success/5 to-transparent" />
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Activity className="w-4 h-4" />
              <span>Current Streak</span>
            </div>
            <div className="text-2xl font-bold">{stats.activeStreak}</div>
            <div className="text-xs text-success mt-1">
              days in a row
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="theme-card p-4 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent" />
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span>Completion Rate</span>
            </div>
            <div className="text-2xl font-bold">{stats.completionRate}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              overall success
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="theme-card p-4 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent" />
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Heart className="w-4 h-4" />
              <span>Best Performing</span>
            </div>
            <div className="text-lg font-bold truncate">
              {stats.bestPerforming || 'N/A'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              highest completion
            </div>
          </div>
        </motion.div>
      </div>

      {/* Completion Trend */}
      <div className="theme-card p-6">
        <h3 className="text-lg font-semibold mb-6">Completion Trend</h3>
        <div className="h-64">
          <Line data={completionData} options={chartOptions} />
        </div>
      </div>

      {/* Insights */}
      <div className="theme-card p-6">
        <h3 className="text-lg font-semibold mb-4">Insights</h3>
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-muted/30 p-4 rounded-lg"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-primary mb-2">
              <TrendingUp className="w-4 h-4" />
              <span>Trending Up</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Your habit completion rate has improved by 15% over the last month.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-muted/30 p-4 rounded-lg"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-success mb-2">
              <TargetIcon className="w-4 h-4" />
              <span>Achievement</span>
            </div>
            <p className="text-sm text-muted-foreground">
              You've maintained a 7-day streak in "Morning Meditation". Keep it up!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-muted/30 p-4 rounded-lg"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-warning mb-2">
              <Brain className="w-4 h-4" />
              <span>Suggestion</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Consider adding reminders for habits with lower completion rates.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}